import React, { useMemo } from 'react';
import { Box, Text } from 'grommet';
import InvoiceTag from '../../../InvoiceTag';
import Button from '../../../Button';
import Loading from '../../../Loading';
import FontAwesomeIcon from '../../../FontAwesomeIcon';
import { formatCurrency } from '../../../../utils/numbers';
import {
    faCircleCheck as IconCheck,
    faPenToSquare as IconEdit,
} from '@fortawesome/pro-light-svg-icons';

const FastPath = ({
    invoices,
    amountToMatch,
    onConfirm,
    onEdit,
    isSubmitting,
}) => {
    const totalMatched = useMemo(
        () => invoices.reduce((sum, inv) => sum + (inv.applied || parseFloat(inv.open_balance) || 0), 0),
        [invoices]
    );

    const isBalanced = totalMatched === amountToMatch;

    return (
        <Box fill gap="medium">
            <Box direction="column" gap="medium" flex>
                {/* Match summary */}
                <Box flex gap="medium" justify="center" align="center" pad="medium">
                    <Box align="center" gap="small">
                        <FontAwesomeIcon
                            SelectedIcon={IconCheck}
                            size="xlarge"
                            color="green-strong"
                        />
                        <Text size="large" weight={500}>
                            Perfect match found
                        </Text>
                        <Text color="text-weak" textAlign="center">
                            We matched {invoices.length} invoice{invoices.length !== 1 ? 's' : ''} totaling{' '}
                            {formatCurrency(totalMatched)} to this {formatCurrency(amountToMatch)} payment.
                        </Text>
                    </Box>

                    {/* Invoice chips */}
                    <Box
                        direction="row"
                        wrap
                        gap={{ row: 'xsmall', column: 'xsmall' }}
                        justify="center"
                        overflow="auto"
                        height={{ max: '150px' }}
                        width={{ max: '600px' }}
                    >
                        {invoices.map(inv => (
                            <InvoiceTag key={inv.id} id={inv.id} />
                        ))}
                    </Box>

                    {/* Summary */}
                    <Box
                        width={{ max: '300px' }}
                        fill="horizontal"
                        gap="xsmall"
                        pad="small"
                        border
                        round="xsmall"
                    >
                        <Box direction="row" justify="between">
                            <Text>Payment</Text>
                            <Text>{formatCurrency(amountToMatch)}</Text>
                        </Box>
                        <Box direction="row" justify="between">
                            <Text>Matched</Text>
                            <Text>{formatCurrency(totalMatched)}</Text>
                        </Box>
                        <Box direction="row" justify="between" border={{ side: 'top' }} pad={{ top: 'xsmall' }}>
                            <Text>Remaining</Text>
                            <Text color={isBalanced ? 'green-strong' : 'status-critical-2'} weight={500}>
                                {formatCurrency((amountToMatch || 0) - totalMatched)}
                            </Text>
                        </Box>
                    </Box>

                    {/* Actions */}
                    <Box direction="row" gap="small" margin={{ top: 'small' }}>
                        <Button
                            event={{ section: 'fast_path', element: 'edit_manually' }}
                            onClick={onEdit}
                            label={
                                <Box direction="row" gap="xsmall" align="center">
                                    <FontAwesomeIcon SelectedIcon={IconEdit} size="xsmall" />
                                    <Text>Edit manually</Text>
                                </Box>
                            }
                        />
                        <Button
                            event={{ section: 'fast_path', element: 'confirm' }}
                            primary
                            disabled={isSubmitting}
                            onClick={() => onConfirm(invoices)}
                            label={isSubmitting ? <Loading plain /> : 'Confirm & apply'}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default FastPath;
